import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { PageSection, PageTitle, PageSubtitle, Paragraph } from "../components/PageLayout";

const OrderedList = styled.ol`
  margin: 0 0 24px 22px;
  padding: 0;
  line-height: 1.6;
`;

const NestedList = styled.ol`
  margin: 12px 0 12px 20px;
  padding: 0;
  line-height: 1.6;
  list-style: lower-alpha;
`;

function Terms() {
  return (
    <main id="main-content">
      <Helmet>
        <title>Texmu – Terms of Use</title>
        <meta name="description" content="Terms governing the Texmu application and browser extensions." />
      </Helmet>
      <PageSection>
        <PageTitle>Texmu – Terms of Use</PageTitle>
        <PageSubtitle>Effective Date: 30 October 2025</PageSubtitle>
        <Paragraph>Developer: “Developer”, “I”, “me”, or “my”.</Paragraph>
        <Paragraph>
          Software: The Texmu application and browser extension(s) (collectively referred to as the “Software” or “Texmu”).
        </Paragraph>
        <Paragraph>
          By installing, accessing, or using the Software, you agree to these Terms of Use (“Terms”). If you do not agree, do not install or use the Software.
        </Paragraph>
        <OrderedList>
          <li>
            <strong>License and Scope</strong>
            <OrderedList>
              <li>
                <strong>License Grant.</strong> Subject to these Terms, I grant you a limited, non-exclusive, non-transferable, and non-sublicensable license to install and use Texmu on compatible devices obtained through official distribution channels (such as the Apple App Store). You may use the Software for both personal and commercial purposes, provided that each installation is properly licensed.
              </li>
              <li>
                <strong>No Transfer of Ownership.</strong> The Software is licensed, not sold. All rights not expressly granted remain my property.
              </li>
              <li>
                <strong>Restrictions.</strong>
                <NestedList>
                  <li>copy, modify, adapt, translate, create derivative works of, decompile, reverse engineer, disassemble, or otherwise attempt to derive the source code of the Software, except as expressly permitted by law;</li>
                  <li>rent, lease, sell, lend, sublicense, distribute, or otherwise transfer any right in the Software;</li>
                  <li>bypass, disable, or interfere with any security or technical protections; or</li>
                  <li>use the Software in any unlawful manner or in violation of these Terms.</li>
                </NestedList>
              </li>
              <li>
                <strong>Updates.</strong> I may provide updates, enhancements, or improvements (“Updates”). Updates may be installed automatically or made available for manual installation. Continued use after an Update constitutes acceptance of the updated Software and Terms.
              </li>
            </OrderedList>
          </li>
          <li>
            <strong>Acceptable Use</strong>
            <Paragraph as="p">
              You agree not to misuse the Software or use it in ways that interfere with normal website operation, introduce harmful code, or manipulate browser functionality beyond Texmu’s intended design (on-device text filtering and blurring).
            </Paragraph>
          </li>
          <li>
            <strong>Third-Party Sites and Services</strong>
            <OrderedList>
              <li>
                <strong>No Responsibility for Third Parties.</strong> Texmu operates locally on your device and may interact with third-party websites. I have no control over and assume no responsibility for third-party websites, their content, or their data practices.
              </li>
              <li>
                <strong>Compatibility.</strong> I do not guarantee compatibility with all browsers, operating systems, or future software versions.
              </li>
              <li>
                <strong>Third-Party Terms.</strong> Your use of browsers, devices, or other third-party services remains subject to their respective terms.
              </li>
            </OrderedList>
          </li>
          <li>
            <strong>Intellectual Property</strong>
            <OrderedList>
              <li>
                <strong>Ownership.</strong> All intellectual property rights in and to Texmu, including its source code, design, graphics, and related materials, are owned by me and protected by applicable laws.
              </li>
              <li>
                <strong>Feedback.</strong> If you provide suggestions or feedback, you grant me a perpetual, irrevocable, royalty-free license to use and incorporate them without restriction.
              </li>
            </OrderedList>
          </li>
          <li>
            <strong>Support, Availability, and Modifications</strong>
            <OrderedList>
              <li>
                <strong>Support.</strong> Support is available via solocreater8919@gmail.com. While I will make reasonable efforts to assist, I cannot guarantee resolution of all issues.
              </li>
              <li>
                <strong>Availability.</strong> The Software may occasionally be unavailable due to maintenance, updates, or technical limitations.
              </li>
              <li>
                <strong>Modifications.</strong> I reserve the right to modify, suspend, or discontinue any feature or the entire Software at any time, with or without notice.
              </li>
            </OrderedList>
          </li>
          <li>
            <strong>App Store Terms</strong>
            <Paragraph as="p">
              If the Software is downloaded or purchased via an app store (e.g., Apple App Store), the store’s terms and policies also apply. Refunds, billing, and payment are managed by the store. I do not control or guarantee app store decisions.
            </Paragraph>
          </li>
          <li>
            <strong>No Professional Advice</strong>
            <Paragraph as="p">
              Texmu provides automated text-blurring functionality only. It does not provide legal, medical, financial, or professional advice. You are solely responsible for how you use the Software.
            </Paragraph>
          </li>
          <li>
            <strong>Warranties and Disclaimers</strong>
            <OrderedList>
              <li>
                <strong>AS-IS Basis.</strong> The Software is provided “AS IS” and “AS AVAILABLE,” without any warranties of any kind, whether express, implied, statutory, or otherwise.
              </li>
              <li>
                <strong>No Implied Warranties.</strong> I disclaim all implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.
              </li>
              <li>
                <strong>No Performance Guarantees.</strong> I do not warrant that Texmu will operate uninterrupted, error-free, or compatible with all websites, devices, or browsers.
              </li>
              <li>
                <strong>Your Responsibility.</strong> You are responsible for ensuring device compatibility, data backups, and proper configuration of your environment.
              </li>
            </OrderedList>
          </li>
          <li>
            <strong>Limitation of Liability</strong>
            <OrderedList>
              <li>
                <strong>Excluded Damages.</strong> I shall not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, including but not limited to loss of data, profits, goodwill, or other intangible losses, even if advised of the possibility of such damages.
              </li>
              <li>
                <strong>Cap on Liability.</strong> My total cumulative liability for any claim arising out of or related to your use of Texmu shall not exceed the amount paid for the app.
              </li>
              <li>
                <strong>Essential Purpose.</strong> These limitations apply even if any remedy fails of its essential purpose.
              </li>
            </OrderedList>
          </li>
          <li>
            <strong>Indemnification</strong>
            <Paragraph as="p">
              You agree to defend, indemnify, and hold harmless the Developer and any affiliates, contractors, or agents from any claims, damages, losses, liabilities, or expenses (including reasonable legal fees) arising from your misuse of the Software, your breach of these Terms, or your violation of any law or third-party rights.
            </Paragraph>
          </li>
          <li>
            <strong>Termination</strong>
            <Paragraph as="p">
              I may suspend or terminate your license to use the Software at any time, without notice, if you breach these Terms or misuse the Software. Upon termination, you must uninstall and cease all use of Texmu. Clauses relating to liability, intellectual property, and disclaimers survive termination.
            </Paragraph>
          </li>
          <li>
            <strong>Compliance and Export</strong>
            <Paragraph as="p">
              You represent that you are not located in a country under embargo or subject to export restrictions. You agree to comply with all applicable export and sanctions laws when using or distributing the Software.
            </Paragraph>
          </li>
          <li>
            <strong>Open-Source Components</strong>
            <Paragraph as="p">
              Texmu may include open-source components governed by their own licenses. Such terms take precedence for those components.
            </Paragraph>
          </li>
          <li>
            <strong>Beta or Experimental Features</strong>
            <Paragraph as="p">
              Any beta or experimental features are provided “AS IS” and may be modified, disabled, or removed at any time without notice.
            </Paragraph>
          </li>
          <li>
            <strong>Consumer Rights</strong>
            <Paragraph as="p">
              Nothing in these Terms limits any mandatory consumer protection rights you may have under local law. If any provision conflicts with such rights, it will apply only to the extent permitted by law.
            </Paragraph>
          </li>
          <li>
            <strong>Governing Law and Jurisdiction</strong>
            <Paragraph as="p">
              These Terms are governed by and construed in accordance with the laws of Austria, excluding its conflict-of-law principles. You agree that any disputes arising out of or relating to these Terms or Texmu shall be brought exclusively before the competent courts of Vienna, Austria. All claims must be made individually and not as part of a class or representative action.
            </Paragraph>
          </li>
          <li>
            <strong>Changes to Terms</strong>
            <Paragraph as="p">
              I may update or revise these Terms from time to time. The “Effective Date” will indicate the latest version. Continued use after any update constitutes acceptance of the revised Terms.
            </Paragraph>
          </li>
          <li>
            <strong>Assignment</strong>
            <Paragraph as="p">
              You may not assign or transfer your rights or obligations under these Terms without my prior written consent. I may assign these Terms without restriction.
            </Paragraph>
          </li>
          <li>
            <strong>Severability and Waiver</strong>
            <Paragraph as="p">
              If any part of these Terms is found unenforceable, that part will be modified to the minimum extent necessary, and the remainder will continue in full force. Failure to enforce any right is not a waiver of that right.
            </Paragraph>
          </li>
          <li>
            <strong>Entire Agreement</strong>
            <Paragraph as="p">
              These Terms constitute the entire agreement between you and the Developer regarding Texmu and supersede all prior communications or understandings. For details on data handling, please refer to the Privacy Policy available in the app or at texmu.dev/privacy.
            </Paragraph>
          </li>
        </OrderedList>
      </PageSection>
    </main>
  );
}

export default Terms;
